import React from "react"
import { Box, Typography, IconButton, styled } from "@mui/material"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import { COLORS } from "../../utils"
import { getExecutionsSidebarColor, PSES_SIDEBAR_WIDTH } from "../../utils/productionStepExecution"


const StyledCard = styled("div", {
	shouldForwardProp: (prop) => prop !== "isSelected"
})(({ isSelected = false, isExpanded = false }) => {
	return {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		padding: 12,
		gap: 12,
		borderRadius: 8,
		cursor: "pointer",
		backgroundColor: isSelected ? COLORS.DOWNLOAD_DOCUMENT_BLUE : "#F0F0F0",
		minWidth: isExpanded ? 190 : 56,
		width: "100%",
		transition: "background-color 0.2s ease, max-width 0.2s ease",
		color: isSelected ? "#FFFFFF" : COLORS.BLACK800,
	}
})

const StyledBadge = styled("div", {
	shouldForwardProp: (prop) => prop !== "isSelected" && prop !== "count"
})(({ isSelected = false, count = 0 }) => {
	let color = getExecutionsSidebarColor(isSelected)

	if (count === 0 && isSelected) {
		color = "#2196F3"
	}
	return {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		width: 32,
		height: 32,
		padding: "4px 8px",
		fontSize: 16,
		fontWeight: (count === 0 && isSelected) ? 500 : 400,
		lineHeight: 1.5,
		borderRadius: 64,
		border: "1px solid #DBDBDB",
		backgroundColor: "#ffffff",
		color,
	}
})

const StyledLabel = styled(Typography, {
	shouldForwardProp: (prop) => prop !== "isSelected"
})(({ isSelected = false }) => {
	return {
		fontSize: 16,
		color: isSelected ? "#FFFFFF" : COLORS.BLACK800,
		fontWeight: 400,
		lineHeight: 1.5,
	}
})

const ExecutionsSidebar = ({
	selected,
	onSelect,
	isExpanded,
	onToggleExpand,
	notifications = {},
	menus = [],
}) => {
	return (
		<>
			<Box
				sx={{
					...styles.sideBarSpacer,
					width: isExpanded ? PSES_SIDEBAR_WIDTH.EXPANDED : PSES_SIDEBAR_WIDTH.COLLAPSED,
				}}
			/>
			<Box
				sx={{
					...styles.sidebarRoot,
					width: isExpanded ? PSES_SIDEBAR_WIDTH.EXPANDED : PSES_SIDEBAR_WIDTH.COLLAPSED,
				}}
			>
				<Box sx={styles.header} className={isExpanded ? "spaceBetween" : "justifyCenter"}>
					{isExpanded && <Typography sx={styles.menuTitle}>Menu</Typography>}
					<IconButton onClick={onToggleExpand} sx={styles.toggleButton}>
						{isExpanded ? <ChevronLeftIcon /> : <ChevronRightIcon />}
					</IconButton>
				</Box>

				<Box sx={styles.menus}>
					{menus.map((menu, index) => {
						const Icon = menu.icon
						const isSelected = selected === menu.id
						const count = notifications[menu.id]?.total || 0

						return (
							<StyledCard
								key={menu.id + index}
								onClick={() => onSelect(menu.id)}
								isSelected={isSelected}
								isExpanded={isExpanded}
							>
								{/* top */}
								<Box className="stretchSelf flexRow center" sx={styles.menuIconContainer}>
									<Icon isSelected={isSelected} />
								</Box>
								{/* bottom */}
								<Box className="stretchSelf flexRow spaceBetween center" sx={{ gap: "12px" }}>
									{isExpanded && (
										<StyledLabel isSelected={isSelected}>
											{menu.label}
										</StyledLabel>
									)}
									<StyledBadge isSelected={isSelected} count={count}>
										{count}
									</StyledBadge>
								</Box>
							</StyledCard>
						)
					})}
				</Box>
			</Box>
		</>
	)
}

const styles = {
	sidebarRoot: {
		display: "flex",
		flexDirection: "column",
		border: `1px solid ${COLORS.BLACK_200}`,
		backgroundColor: "white",
		transition: "width 0.2s ease",
		flexShrink: 0,
		height: "100%",
		position: "fixed",
		left: 0,
		zIndex: 900,
	},
	sideBarSpacer: {
		transition: "width 0.2s ease",
		flexShrink: 0,
	},
	header: {
		display: "flex",
		alignItems: "center",
		padding: "16px",
		borderBottom: `1px solid ${COLORS.BLACK_200}`,
		minHeight: "56px",
	},
	menuTitle: {
		fontSize: "16px",
		fontWeight: 500,
		color: COLORS.BLACK800,
	},
	toggleButton: {
		padding: "4px",
		color: COLORS.BLACK_500,
	},
	menus: {
		display: "flex",
		flexDirection: "column",
		padding: "8px 16px",
		gap: "12px",
		marginTop: "8px",
	},
	menuIconContainer: {
		width: 32,
		height: 32,
		backgroundColor: "#FFFFFF",
		borderRadius: "4px",
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
	}
}

export default ExecutionsSidebar
